import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { mbTextStyles } from '@/utils/general/style-utils';
import { MB_Image } from '@/components/general/image';
import { MB_OnHoverWrapper } from '@/components/general/on-hover-wrapper';
import { GridContextProvider, GridDropZone, swap } from '../../../../../externalLibraries/react-grid-dnd';
import { DragIcon } from '../../../../resources/svgComponents/DragIcon';
import { COLORS } from '../../../../constants/colors';
import { textStyles } from '../../../../constants/textStyles';
import { imageUtils } from '../../../../utils/imageUtils';
import { PoolImage, PoolVideo, RESOURCE_POOL_TYPE } from '../../../../typesAndInterfaces/typesAndInterfaces';

/** Seed spacing; each slot leaves room for ~10 fractional inserts before a reseed. */
const ORDER_GAP = 1024;

type PoolItem = PoolImage | PoolVideo;

interface FolderReorderGridProps {
  resourcePoolType: RESOURCE_POOL_TYPE;
  items: PoolItem[];
  boxesPerRow: number;
  /**
   * Pool-folder mode: emits per-item numeric `order` updates (gap strategy below).
   * Required unless `onOrderChange` is given.
   */
  onPersistOrder?: (itemsOrder: { itemId: string; order: number }[]) => void;
  /**
   * Ordered-list mode: emits the full ordered id array after every drop and
   * skips the numeric `order` bookkeeping. Used where the order is an array on
   * the parent document (e.g. a Lessons collection's `extras`) rather than a
   * field on each item.
   */
  onOrderChange?: (orderedIds: string[]) => void;
}

/**
 * Drag-to-reorder grid for a folder's items (web only — the vendored
 * react-grid-dnd renders DOM). Ordering is DESCENDING: higher `order` = earlier.
 *
 * Persistence (pool-folder mode, `onPersistOrder`) follows the gap strategy:
 * - if any item in the folder has no order yet, the first drop seeds the whole
 *   folder once (order = (N - index) * GAP);
 * - after that, each drop persists ONLY the moved item, using the midpoint of
 *   its new neighbors' values;
 * - if a gap is exhausted (neighbors closer than 1), the folder is reseeded.
 *
 * In ordered-list mode (`onOrderChange`) the grid only reports the new id order.
 */
const FolderReorderGrid = ({ resourcePoolType, items, boxesPerRow, onPersistOrder, onOrderChange }: FolderReorderGridProps) => {
  const [orderedItems, setOrderedItems] = useState<PoolItem[]>(items);

  useEffect(() => {
    setOrderedItems(items);
  }, [items]);

  const onChange = useCallback(
    (_sourceId: string, sourceIndex: number, targetIndex: number) => {
      if (sourceIndex === targetIndex) {
        return;
      }
      const nextState = swap(orderedItems, sourceIndex, targetIndex) as PoolItem[];

      if (onOrderChange) {
        setOrderedItems(nextState);
        onOrderChange(nextState.map(item => item._id));
        return;
      }

      const needsSeed = nextState.some(item => item.order === undefined);
      const prevNeighbor = nextState[targetIndex - 1];
      const nextNeighbor = nextState[targetIndex + 1];

      let updates: { itemId: string; order: number }[];
      let localOrders: Map<string, number>;

      if (needsSeed || isGapExhausted(prevNeighbor, nextNeighbor)) {
        updates = nextState.map((item, index) => ({ itemId: item._id, order: (nextState.length - index) * ORDER_GAP }));
        localOrders = new Map(updates.map(update => [update.itemId, update.order]));
      } else {
        const upperBound = prevNeighbor?.order !== undefined ? prevNeighbor.order : (nextNeighbor?.order ?? 0) + 2 * ORDER_GAP;
        const lowerBound = nextNeighbor?.order !== undefined ? nextNeighbor.order : upperBound - 2 * ORDER_GAP;
        const midpoint = (upperBound + lowerBound) / 2;
        updates = [{ itemId: nextState[targetIndex]._id, order: midpoint }];
        localOrders = new Map([[nextState[targetIndex]._id, midpoint]]);
      }

      setOrderedItems(nextState.map(item => (localOrders.has(item._id) ? { ...item, order: localOrders.get(item._id) } : item)));
      onPersistOrder?.(updates);
    },
    [onOrderChange, onPersistOrder, orderedItems]
  );

  const keyExtractor = useCallback((item: PoolItem) => item._id, []);

  const renderItem = useCallback(
    ({ item, isDragging }: { item: PoolItem; isDragging: boolean }) => {
      const thumbnailSource =
        resourcePoolType === RESOURCE_POOL_TYPE.Image ? (item as PoolImage).small ?? imageUtils.images.brokenImage : (item as PoolVideo).thumbnail?.small ?? imageUtils.images.brokenImage;

      return (
        <MB_OnHoverWrapper>
          {({ hovered }) => (
            <View style={[styles.card, (isDragging || hovered) && styles.cardDragging]}>
              {hovered && <DragIcon style={styles.dragIcon} />}
              <MB_Image source={thumbnailSource} style={styles.thumbnail} resizeMode="contain" disableInteraction />
              <Text style={styles.cardTitle} numberOfLines={2}>
                {item.name}
              </Text>
            </View>
          )}
        </MB_OnHoverWrapper>
      );
    },
    [resourcePoolType]
  );

  return (
    <GridContextProvider onChange={onChange}>
      <GridDropZone
        id="folder-items"
        data={orderedItems}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        boxesPerRow={boxesPerRow}
        rowHeight={190}
        style={{ height: Math.ceil(orderedItems.length / boxesPerRow) * 190, borderRadius: 8 }}
      />
    </GridContextProvider>
  );
};

const isGapExhausted = (prevNeighbor?: PoolItem, nextNeighbor?: PoolItem) => {
  if (prevNeighbor?.order === undefined || nextNeighbor?.order === undefined) {
    return false;
  }
  return Math.abs(prevNeighbor.order - nextNeighbor.order) < 1;
};

export { FolderReorderGrid };

const styles = StyleSheet.create({
  card: {
    width: 144,
    height: 170,
    borderRadius: 8,
    backgroundColor: COLORS.backgroundPurple,
    alignItems: 'center',
    paddingTop: 10,
    paddingHorizontal: 8,
  },
  cardDragging: {
    backgroundColor: COLORS.cardPurple,
  },
  dragIcon: {
    position: 'absolute',
    left: 6,
    top: 6,
    zIndex: 1,
  },
  thumbnail: {
    width: 110,
    height: 110,
  },
  cardTitle: mbTextStyles([
    textStyles.smallText,
    {
      marginTop: 8,
    },
  ]),
});
